/**
 * ApiAdapter.ts — WebSocket-backed BackendAdapter.
 *
 * Connects to a backend that speaks the wire format defined in
 * `engine/types.ts` (ServerMessage / ClientMessage). The FastAPI backend
 * (`backend/server.py`) is the reference implementation; any backend that
 * speaks the same shapes works unmodified.
 *
 * Capabilities:
 *  - Full editor surface (addNode/removeEdge/setBends/etc.) via the generic
 *    `{type:'edit', op, args}` envelope. The server dispatches each op to its
 *    own LocalAdapter-equivalent and broadcasts `{kind:'bundleUpdate'}`.
 *  - Value-returning ops (duplicateNode, renameNode, renameEdge, exportBundle)
 *    use a reqId correlation: the outbound edit carries `reqId`, the server
 *    replies with `{kind:'result', reqId, result}`. ApiAdapter returns a
 *    Promise from these four ops; fire-and-forget ops return void.
 *  - `onBundleChange` fires on `{kind:'bundleUpdate'}`; `onError` fires on
 *    `{kind:'error'}`.
 *  - Connection status is surfaced via `onConnection` (set up before
 *    `loadScenario` so the UI can show "connecting…" → "connected").
 *
 * The API key rides as a `?key=` query param on the WS URL. Browsers don't
 * allow arbitrary headers on `new WebSocket()`, so the subprotocol / query-param
 * routes are the only options; query param is the simpler + more debuggable.
 */
import type {
  Bend,
  ClientMessage,
  EditMessage,
  EditOpName,
  GraphEdge,
  GraphNode,
  KnobPin,
  LegendEntry,
  MetricSpec,
  NodeGroup,
  ProjectBundle,
  ScenarioBundle,
  ServerMessage,
  TickPayload,
  WorkflowEventMessage,
  TriggerEventMessage,
  WorkflowTrigger,
} from './types';
import { MAIN_GRAPH_ID } from './types';
import type { BackendAdapter } from './adapter';

export type ConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'error';

export interface ApiAdapterOptions {
  url: string;
  /** Bearer/`?key=` credential. OPTIONAL — keyless is a supported setup (the
   *  Connectivity wizard's key field is optional; local dev backends run with
   *  no bearer check). Omitted → the WS URL carries no `key` param. */
  apiKey?: string;
  /** Initial scenario id to load on connect. The server's first
   *  `{kind:'bundle'}` is for this scenario. */
  scenarioId: string;
  /** Tick rate hint for the server (the server is the clock; this is advisory). */
  hz?: number;
  /** Which execution surface to ask the backend for.
   *  - `'demo'` (default): the backend's pump streams recorded ticks. The
   *    ApiAdapter's start/stop drive the pump.
   *  - `'relay'` (Phase D): the backend starts no clock; ticks arrive only
   *    from external `POST /api/events/{id}`. start/stop become no-ops on
   *    the wire (kept for client compat); the UI swaps play/pause for a
   *    "Listening for events" indicator + a manual POST tool. */
  mode?: 'demo' | 'relay';
}

/** Editor ops that return a value and therefore need reqId correlation. The
 *  server must reply with `{kind:'result', reqId, result}` for these four.
 *  Everything else is fire-and-forget — the `bundleUpdate` broadcast is the
 *  signal that the edit landed. */
const VALUE_RETURNING_OPS: ReadonlySet<EditOpName> = new Set<EditOpName>([
  'duplicateNode',
  'renameNode',
  'renameEdge',
  'renameGroup',
  'exportBundle',
]);

export class ApiAdapter implements BackendAdapter {
  private readonly url: string;
  private readonly apiKey: string | undefined;
  private readonly scenarioId: string;
  private readonly hz: number;
  /** `'relay'` → the backend starts no clock; ticks come from external POSTs.
   *  `'demo'` → the backend's pump drives ticks. */
  private readonly mode: 'demo' | 'relay';

  private ws: WebSocket | null = null;
  /** The full graph collection (delivered by the {kind:'project'} handshake).
   *  Null until the first project/bundle frame arrives. */
  private bundles: ScenarioBundle[] | null = null;
  /** The active graph id (which one the editor canvas shows). Per-client UI
   *  state — not broadcast; defaults to Main. */
  private activeId: string = MAIN_GRAPH_ID;
  private running = false;

  private tickSubs = new Set<(tick: TickPayload) => void>();
  private bundleSubs = new Set<() => void>();
  private errorSubs = new Set<(msg: string) => void>();
  private paramSubs = new Set<(path: string, value: number) => void>();
  /** State-hydrate subscribers — relay clients adopting the backend's current
   *  stocks + step as their baseline (avoids conservation failure on the first
   *  tick after a mid-run connect). */
  private stateSubs = new Set<(step: number, stocks: Record<string, number>) => void>();
  private workflowSubs = new Set<(msg: WorkflowEventMessage) => void>();
  private triggerSubs = new Set<(msg: TriggerEventMessage) => void>();
  private connectionCbs = new Set<(s: ConnectionStatus) => void>();

  /** Outbound queue — flushed whenever the socket is open. Lets callers send
   *  before `loadScenario` resolves (rare but safe). */
  private outbound: ClientMessage[] = [];
  /** Pending reqId → resolver map for value-returning edit ops. */
  private pending = new Map<number, (value: unknown) => void>();
  private nextReqId = 1;

  constructor(opts: ApiAdapterOptions) {
    this.url = opts.url;
    this.apiKey = opts.apiKey;
    this.scenarioId = opts.scenarioId;
    this.hz = opts.hz ?? 4;
    this.mode = opts.mode ?? 'demo';
  }

  /** Which execution surface the adapter asked the backend for. The UI reads
   *  this to swap the demo play/pause controls for a "Listening for events"
   *  indicator in relay mode. */
  getMode(): 'demo' | 'relay' {
    return this.mode;
  }

  // ── Connection lifecycle ──────────────────────────────────────────────────
  /** Register a connection-status listener. Returns an unsubscribe fn. Set up
   *  before `loadScenario` so the UI catches the initial "connecting" event. */
  onConnection(cb: (status: ConnectionStatus) => void): () => void {
    this.connectionCbs.add(cb);
    return () => this.connectionCbs.delete(cb);
  }

  private setConnection(status: ConnectionStatus): void {
    for (const cb of this.connectionCbs) cb(status);
  }

  async loadScenario(): Promise<ProjectBundle> {
    return new Promise<ProjectBundle>((resolve, reject) => {
      // Compose the URL with the API key, requested scenario id, hz hint, and
      // execution mode as query params. The mock server reads `scenario` to
      // pick the initial bundle; the Python backend reads `mode` to decide
      // whether to start its demo pump (`replay`) or wait for relay POSTs
      // (`relay` / legacy `live`).
      const query: Record<string, string> = {
        scenario: this.scenarioId,
        hz: String(this.hz),
      };
      if (this.apiKey) query.key = this.apiKey;
      if (this.mode === 'relay') query.mode = 'relay';
      const fullUrl = this.appendQuery(this.url, query);
      this.setConnection('connecting');
      let opened = false;
      try {
        this.ws = new WebSocket(fullUrl);
      } catch (e) {
        this.setConnection('error');
        reject(new Error(`Cannot open WebSocket: ${e instanceof Error ? e.message : String(e)}`));
        return;
      }
      const ws = this.ws;

      ws.onopen = () => {
        opened = true;
        this.setConnection('connected');
        this.flushOutbound();
      };
      ws.onerror = () => {
        this.setConnection('error');
        if (!this.bundles) {
          reject(new Error('WebSocket error before project arrived'));
        }
      };
      ws.onclose = () => {
        // If we never connected, surface as error; otherwise it's a clean drop.
        if (!opened) this.setConnection('error');
        else if (this.bundles) this.setConnection('disconnected');
      };
      ws.onmessage = (ev: MessageEvent) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(ev.data as string) as ServerMessage;
        } catch {
          return; // ignore malformed frames
        }
        this.handleMessage(msg, resolve);
      };
    });
  }

  /** Central inbound dispatcher. `resolveLoad` is wired only for the first
   *  `{kind:'project'}` (or legacy `{kind:'bundle'}`); later graph events just
   *  refresh state + fire onBundleChange. */
  private handleMessage(msg: ServerMessage, resolveLoad?: (p: ProjectBundle) => void): void {
    switch (msg.kind) {
      case 'project': {
        const isFirst = !this.bundles;
        this.bundles = msg.graphs;
        if (isFirst && resolveLoad) resolveLoad({ graphs: msg.graphs });
        else for (const cb of this.bundleSubs) cb();
        break;
      }
      case 'bundle': {
        // Legacy single-graph handshake (back-compat with older backends):
        // treat as a one-graph Main project.
        const isFirst = !this.bundles;
        this.bundles = [{ ...msg.bundle, id: MAIN_GRAPH_ID, label: 'Main' }];
        if (isFirst && resolveLoad) resolveLoad({ graphs: this.bundles });
        else for (const cb of this.bundleSubs) cb();
        break;
      }
      case 'tick': {
        // In demo mode, ticks are gated by `running` (the play/pause button
        // starts/stops the backend pump). In relay mode the backend starts no
        // clock — ticks arrive unsolicited from external POSTs whenever the
        // external model advances — so forward them regardless of `running`.
        if (this.mode === 'relay' || this.running) {
          for (const fn of this.tickSubs) fn(msg.tick);
        }
        break;
      }
      case 'graphUpdate': {
        // One graph's bundle changed (a per-graph edit landed on this graphId).
        this.replaceGraph(msg.graphId, msg.bundle);
        if (msg.graphId === this.activeId) for (const cb of this.bundleSubs) cb();
        break;
      }
      case 'graphAdded': {
        if (this.bundles) this.bundles = [...this.bundles, msg.bundle];
        for (const cb of this.bundleSubs) cb();
        break;
      }
      case 'graphRemoved': {
        if (this.bundles) this.bundles = this.bundles.filter((b) => b.id !== msg.graphId);
        if (this.activeId === msg.graphId) this.activeId = MAIN_GRAPH_ID;
        for (const cb of this.bundleSubs) cb();
        break;
      }
      case 'bundleUpdate': {
        // Legacy single-graph update (back-compat) → treat as a Main update.
        this.replaceGraph(MAIN_GRAPH_ID, msg.bundle);
        if (this.activeId === MAIN_GRAPH_ID) for (const cb of this.bundleSubs) cb();
        break;
      }
      case 'error': {
        for (const cb of this.errorSubs) cb(msg.message);
        break;
      }
      case 'result': {
        const resolver = this.pending.get(msg.reqId);
        if (resolver) {
          this.pending.delete(msg.reqId);
          resolver(msg.result);
        }
        break;
      }
      case 'paramChange': {
        // A knob move originated elsewhere — a peer's setParam, or an agent's
        // fenced delta on the relay path. Fan out to whoever wants to track
        // the live param state (the closed control loop's readers).
        for (const cb of this.paramSubs) cb(msg.path, msg.value);
        break;
      }
      case 'state': {
        // Backend snapshot of the current run position (step + prev stocks).
        // A relay client connecting mid-run adopts this as its baseline so the
        // next tick's conservation check passes (its prev would otherwise be 0).
        for (const cb of this.stateSubs) cb(msg.step, msg.stocks);
        break;
      }
      case 'workflowEvent': {
        // Step I/O capture from the workflow worker's debugger gate, relayed
        // through the backend's broadcast. The Workflows tab consumes these.
        for (const cb of this.workflowSubs) cb(msg);
        break;
      }
      case 'triggerEvent': {
        // A ⚡ trigger (or agent triggerWorkflow grant) fired or failed on the
        // relay path. The TriggersPanel / node badge / Workflows rail render
        // live trigger state from these.
        for (const cb of this.triggerSubs) cb(msg);
        break;
      }
      case 'list':
      case 'resetOk':
        // Currently unused by the adapter; reserved for future menu actions.
        break;
    }
  }

  /** Swap one graph's bundle in the collection (no-op if the id is unknown). */
  private replaceGraph(graphId: string, bundle: ScenarioBundle): void {
    if (!this.bundles) {
      this.bundles = [bundle];
      return;
    }
    this.bundles = this.bundles.map((b) => (b.id === graphId ? bundle : b));
  }

  // ── Required interface ────────────────────────────────────────────────────
  subscribe(onTick: (tick: TickPayload) => void): () => void {
    this.tickSubs.add(onTick);
    return () => this.tickSubs.delete(onTick);
  }

  onBundleChange(cb: () => void): () => void {
    this.bundleSubs.add(cb);
    return () => this.bundleSubs.delete(cb);
  }

  onError(cb: (message: string) => void): () => void {
    this.errorSubs.add(cb);
    return () => this.errorSubs.delete(cb);
  }

  /** Register a callback for inbound knob-change broadcasts. Fires on every
   *  `{kind:'paramChange'}` frame — either a peer's `setParam` or an agent's
   *  fenced `paramDelta` (Phase D relay path). The closed control loop's
   *  feedback half: an external model that drives the relay reads these to
   *  decide its next tick. */
  onParamChange(cb: (path: string, value: number) => void): () => void {
    this.paramSubs.add(cb);
    return () => this.paramSubs.delete(cb);
  }

  /** Register a callback for the relay-path state snapshot (`{kind:'state'}`).
   *  Fires once on connect (after the bundle) when the backend has accumulated
   *  state; the client adopts `step` + `stocks` as its baseline. */
  onStateHydrate(cb: (step: number, stocks: Record<string, number>) => void): () => void {
    this.stateSubs.add(cb);
    return () => this.stateSubs.delete(cb);
  }

  /** Register a callback for workflow step events (`{kind:'workflowEvent'}`)
   *  broadcast by the backend's worker-gate webhook (step input/output/error
   *  captures). The Workflows tab merges these with the platform's polled
   *  event history for live step I/O. */
  onWorkflowEvent(cb: (msg: WorkflowEventMessage) => void): () => void {
    this.workflowSubs.add(cb);
    return () => this.workflowSubs.delete(cb);
  }

  /** Register a callback for trigger fire/fail events (`{kind:'triggerEvent'}`)
   *  broadcast by the relay path — one frame per ⚡ trigger (or agent
   *  triggerWorkflow grant) fire attempt, successful or not. The TriggersPanel
   *  + node badge render live trigger status from these. */
  onTriggerEvent(cb: (msg: TriggerEventMessage) => void): () => void {
    this.triggerSubs.add(cb);
    return () => this.triggerSubs.delete(cb);
  }

  setParam(path: string, value: number): void {
    this.send({ type: 'setParam', path, value });
  }

  start(): void {
    this.running = true;
    this.send({ type: 'start' });
  }

  stop(): void {
    this.running = false;
    this.send({ type: 'stop' });
  }

  reset(): void {
    this.send({ type: 'reset' });
  }

  dispose(): void {
    this.tickSubs.clear();
    this.bundleSubs.clear();
    this.errorSubs.clear();
    this.paramSubs.clear();
    this.stateSubs.clear();
    this.connectionCbs.clear();
    this.pending.clear();
    this.ws?.close();
    this.ws = null;
    this.setConnection('disconnected');
  }

  // ── Editor ops ────────────────────────────────────────────────────────────
  // Each is shipped as `{type:'edit', op, args}`. Value-returning ops attach a
  // reqId and return a Promise; the rest return void (the server's
  // `bundleUpdate` broadcast is the confirmation).

  addNode = (node: GraphNode): void => this.editVoid('addNode', [node]);
  updateNode = (node: GraphNode): void => this.editVoid('updateNode', [node]);
  removeNode = (nodeId: string): void => this.editVoid('removeNode', [nodeId]);
  duplicateNode = (nodeId: string): string | null =>
    this.editReturning<string | null>('duplicateNode', [nodeId]);
  renameNode = (oldId: string, newId: string): boolean =>
    this.editReturning<boolean>('renameNode', [oldId, newId]);

  addEdge = (edge: GraphEdge): void => this.editVoid('addEdge', [edge]);
  updateEdge = (edge: GraphEdge): void => this.editVoid('updateEdge', [edge]);
  removeEdge = (edgeId: string): void => this.editVoid('removeEdge', [edgeId]);
  renameEdge = (oldId: string, newId: string): boolean =>
    this.editReturning<boolean>('renameEdge', [oldId, newId]);

  setEdgeGroup = (edgeId: string, group: string | undefined): void =>
    this.editVoid('setEdgeGroup', [edgeId, group]);
  updateEdgeHandles = (
    edgeId: string,
    next: { source?: string; target?: string; sourceHandle?: string; targetHandle?: string },
  ): void => this.editVoid('updateEdgeHandles', [edgeId, next]);

  setBends = (edgeId: string, bends: Bend[]): void =>
    this.editVoid('setBends', [edgeId, bends]);
  setLabelT = (edgeId: string, t: number): void =>
    this.editVoid('setLabelT', [edgeId, t]);

  addGroup = (group: string): void => this.editVoid('addGroup', [group]);

  // Spatial node groups (the GroupRect rectangles). Same ops as the
  // LocalAdapter surface — without these, every group edit (modal rename/
  // colour/metrics, canvas drag/resize, context-menu create/delete) silently
  // no-ops in API mode because the methods are optional on the adapter.
  addNodeGroup = (group: NodeGroup): void => this.editVoid('addNodeGroup', [group]);
  updateNodeGroup = (group: NodeGroup): void => this.editVoid('updateNodeGroup', [group]);
  removeNodeGroup = (groupId: string): void => this.editVoid('removeNodeGroup', [groupId]);
  renameGroup = (oldId: string, newId: string): boolean =>
    this.editReturning<boolean>('renameGroup', [oldId, newId]);

  addMetric = (metric: MetricSpec): void => this.editVoid('addMetric', [metric]);
  updateMetric = (metric: MetricSpec): void => this.editVoid('updateMetric', [metric]);
  removeMetric = (key: string): void => this.editVoid('removeMetric', [key]);
  renameMetric = (oldKey: string, newKey: string): void => this.editVoid('renameMetric', [oldKey, newKey]);

  setParamKey = (
    target: 'node' | 'edge',
    id: string,
    oldKey: string,
    newKey: string,
  ): void => this.editVoid('setParamKey', [target, id, oldKey, newKey]);

  setLegend = (legend: LegendEntry[]): void =>
    this.editVoid('setLegend', [legend]);

  setKnobs = (knobs: KnobPin[]): void =>
    this.editVoid('setKnobs', [knobs]);

  setWorkflowTriggers = (triggers: WorkflowTrigger[]): void =>
    this.editVoid('setWorkflowTriggers', [triggers]);

  replaceBundle = (bundle: ScenarioBundle): void =>
    this.editVoid('replaceBundle', [bundle]);

  exportBundle = (): ScenarioBundle => {
    // Synchronous on the interface (callers don't await). We send the request
    // and return the last-known active bundle immediately; the server's reply
    // will refresh via graphUpdate if anything changed.
    const active = this.activeBundle();
    if (!active) throw new Error('exportBundle before project loaded');
    // Fire the request so the server can confirm; ignore the result.
    void this.editReturning<ScenarioBundle>('exportBundle', []);
    return active;
  };

  // ── Multi-graph (graph collection) ────────────────────────────────────────
  graphIds = (): string[] => (this.bundles ?? []).map((b) => b.id);
  activeGraphId = (): string => this.activeId;
  setActiveGraph = (graphId: string): void => {
    // Per-client UI state — not sent over the wire. The next per-graph edit
    // carries the new active id as its graphId.
    if (this.bundles?.some((b) => b.id === graphId)) this.activeId = graphId;
  };
  addGraph = (name: string): string => {
    // Collection op — no graphId (operates on the whole project). The server
    // replies with {kind:'graphAdded'} carrying the minted id.
    this.editVoid('addGraph', [name], /* collection */ true);
    // Optimistic placeholder id (slug); the broadcast refreshes with the truth.
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'graph';
  };
  removeGraph = (graphId: string): void => {
    if (graphId === MAIN_GRAPH_ID) return;
    this.editVoid('removeGraph', [graphId], /* collection */ true);
  };
  renameGraph = (graphId: string, name: string): string => {
    this.editVoid('renameGraph', [graphId, name], /* collection */ true);
    return graphId === MAIN_GRAPH_ID
      ? MAIN_GRAPH_ID
      : name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'graph';
  };

  /** The active graph's bundle, or null before the project loads. */
  private activeBundle(): ScenarioBundle | null {
    if (!this.bundles || this.bundles.length === 0) return null;
    return this.bundles.find((b) => b.id === this.activeId) ?? this.bundles[0];
  }

  /** The current full graph collection (latest handshake/broadcast state). */
  getProject = (): ProjectBundle | null => (this.bundles ? { graphs: this.bundles } : null);

  // ── Internals ─────────────────────────────────────────────────────────────
  /** Ship a per-graph edit op. `graphId` defaults to the active graph; the
   *  server applies it there. Collection ops pass `collection=true` and omit
   *  graphId (they mutate the whole project, not one graph). */
  private editVoid(op: EditOpName, args: unknown[], collection = false): void {
    const msg: EditMessage = collection
      ? { type: 'edit', op, args }
      : { type: 'edit', op, args, graphId: this.activeId };
    this.send(msg);
  }

  private editReturning<T>(op: EditOpName, args: unknown[]): T {
    if (!VALUE_RETURNING_OPS.has(op)) {
      // Defensive — should never happen given the call sites.
      this.editVoid(op, args);
      return undefined as unknown as T;
    }
    const reqId = this.nextReqId++;
    const msg: EditMessage = { type: 'edit', op, args, graphId: this.activeId, reqId };
    // Promise stored so a future truly-async caller could await; sync callers
    // (the four value-returning ops above) read the active bundle / treat the
    // call as best-effort. For duplicateNode/renameNode/renameEdge, the
    // optimistic return is null/false — the graphUpdate broadcast will refresh
    // the UI with the truth a few ms later.
    this.pending.set(reqId, () => {
      /* result observed; graphUpdate will carry the canonical state */
    });
    this.send(msg);
    // Optimistic placeholder. Callers that need the real result must wait for
    // the graphUpdate broadcast (they always do — the UI re-renders from it).
    return undefined as unknown as T;
  }

  private send(msg: ClientMessage): void {
    this.outbound.push(msg);
    this.flushOutbound();
  }

  private flushOutbound(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.outbound.length) return;
    for (const msg of this.outbound) this.ws.send(JSON.stringify(msg));
    this.outbound = [];
  }

  /** Append query params to a URL, skipping any with empty values. */
  private appendQuery(url: string, params: Record<string, string>): string {
    const entries = Object.entries(params).filter(([, v]) => v !== '');
    if (!entries.length) return url;
    const qs = entries.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}${qs}`;
  }
}
